import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import { LocalHospital, PendingActions, Analytics } from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import PendingIPDApprovals from '../components/ipdDone/PendingIPDApprovals';
import IPDStatistics from '../components/ipdDone/IPDStatistics';
import MyIPDRequests from '../components/ipdDone/MyIPDRequests';
import { ipdDoneService } from '../services/ipdDone';

const IPDDone = () => {
  const { user } = useSelector((state) => state.auth);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Determine available tabs based on user role
  const getAvailableTabs = () => {
    const baseTabs = [
      { label: 'Statistics', icon: <Analytics />, component: IPDStatistics }
    ];

    if (user?.role === 'manager' || user?.role === 'admin') {
      baseTabs.unshift({
        label: `Pending Approvals ${pendingCount > 0 ? `(${pendingCount})` : ''}`,
        icon: <PendingActions />,
        component: PendingIPDApprovals
      });
    }

    if (user?.role === 'bda' || user?.role === 'team_lead' || user?.role === 'assistant_manager') {
      baseTabs.push({
        label: 'My Requests',
        icon: <LocalHospital />,
        component: MyIPDRequests
      });
    }

    return baseTabs;
  };

  const tabs = getAvailableTabs();

  useEffect(() => {
    // Load pending count for managers/admins
    if (user?.role === 'manager' || user?.role === 'admin') {
      loadPendingCount();
    }
  }, [user]);

  const loadPendingCount = async () => {
    try {
      const data = await ipdDoneService.getPendingIPDApprovals();
      setPendingCount(data.total_count || 0);
    } catch (error) {
      console.error('Failed to load pending count:', error);
    }
  };

  const handleApprove = async (leadId) => {
    try {
      setLoading(true);
      const result = await ipdDoneService.approveIPDRequest(leadId);
      showSnackbar(result.message || 'IPD Done request approved successfully', 'success');
      loadPendingCount(); // Refresh count
      return result;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to approve IPD request';
      showSnackbar(message, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (leadId, newStatus = 'followup') => {
    try {
      setLoading(true);
      const result = await ipdDoneService.rejectIPDRequest(leadId, newStatus);
      showSnackbar(result.message || 'IPD Done request rejected', 'success');
      loadPendingCount(); // Refresh count
      return result;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reject IPD request';
      showSnackbar(message, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRequestIPD = async (leadId) => {
    try {
      setLoading(true);
      const result = await ipdDoneService.requestIPDDone(leadId);

      if (result.status === 'pending') {
        showSnackbar('IPD Done request submitted for approval', 'info');
      } else if (result.status === 'ipd_done') {
        showSnackbar('IPD Done status set successfully', 'success');
      }

      return result;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to request IPD Done';
      showSnackbar(message, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  const renderTabContent = () => {
    const CurrentComponent = tabs[tabValue]?.component;
    if (!CurrentComponent) return null;

    const commonProps = {
      onApprove: handleApprove,
      onReject: handleReject,
      onRequestIPD: handleRequestIPD,
      loading,
      onError: setError,
    };

    return <CurrentComponent {...commonProps} />;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospital color="primary" />
          IPD Done Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage IPD Done requests, approvals, and track performance statistics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Role-based Dashboard Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospital color="primary" />
                <Typography variant="h6">Your Role</Typography>
              </Box>
              <Chip
                label={user?.role?.toUpperCase()}
                color="primary"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {(user?.role === 'manager' || user?.role === 'admin') && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingActions color="warning" />
                  <Typography variant="h6">Pending Approvals</Typography>
                </Box>
                <Typography variant="h4" color="warning.main" sx={{ mt: 1 }}>
                  {pendingCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <CardContent>
          {loading && tabValue === 0 ? (
            <LoadingSpinner message="Loading IPD data..." />
          ) : (
            renderTabContent()
          )}
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IPDDone;